import {
  createMetaSiteFromTemplate,
  getUrl,
} from '@wix/ambassador-metasite-api-v1-meta-site/http';
import type { CreateMetaSiteFromTemplateRequest } from '@wix/ambassador-metasite-api-v1-meta-site/types';
import { UrlFilter } from '@wix/ambassador-metasite-api-v1-meta-site/types';
import { distribute } from '@wix/ambassador-document-management-document-store-v1-transaction/http';
import { CliError, CliErrorCode } from '@wix/cli-error';
import type { HttpClient } from '@wix/cli-http-client';
import {
  clientSpecMapSchema,
  createMetaSiteFromTemplateSchema,
  getMetaSiteUrlSchema,
} from './schemas.js';

export class MetasiteManagerClient {
  constructor(private readonly httpClient: HttpClient) {}

  private getClientSpecMap = async (metaSiteId: string) => {
    try {
      const { data } = await this.httpClient.request({
        url: `/_api/msm/v1/meta-site/editor-client-spec-map/${metaSiteId}`,
      });

      return clientSpecMapSchema.parse(data);
    } catch (e) {
      throw new CliError({
        code: CliErrorCode.FailedToGetClientSpecMap(),
        cause: e,
      });
    }
  };

  public createMetaSiteFromTemplate = async (
    request: CreateMetaSiteFromTemplateRequest
  ) => {
    try {
      const { data } = await this.httpClient.request(
        createMetaSiteFromTemplate(request)
      );
      return createMetaSiteFromTemplateSchema.parse(data).metaSite;
    } catch (e) {
      throw new CliError({
        code: CliErrorCode.FailedToCreateMetaSiteFromTemplate(),
        cause: e,
      });
    }
  };

  public getSiteInstalledApps = async (metaSiteId: string) => {
    try {
      const clientSpecMap = await this.getClientSpecMap(metaSiteId);

      return Object.values(clientSpecMap)
        .filter((spec) => spec.appDefinitionId && !spec.permissions?.revoked)
        .map((spec) => spec.appDefinitionId!);
    } catch (e) {
      throw new CliError({
        code: CliErrorCode.FailedToGetSiteInstalledApps(),
        cause: e,
      });
    }
  };

  public getInstalledAppInstanceId = async ({
    metaSiteId,
    appId,
  }: {
    metaSiteId: string;
    appId: string;
  }) => {
    try {
      const clientSpecMap = await this.getClientSpecMap(metaSiteId);
      const installedApp = Object.values(clientSpecMap).find(
        (spec) => spec.appDefinitionId === appId
      );

      return installedApp?.instanceId ?? '';
    } catch (e) {
      throw new CliError({
        code: CliErrorCode.FailedToGetSiteInstalledAppInstanceId(),
        cause: e,
      });
    }
  };

  public getMetaSiteUrl = async () => {
    try {
      const { data } = await this.httpClient.request(
        getUrl({ urlFilter: UrlFilter.NO_URL_TYPE_FILTER })
      );
      return getMetaSiteUrlSchema.parse(data);
    } catch (e) {
      throw new CliError({
        code: CliErrorCode.FailedToGetUrl(),
        cause: e,
      });
    }
  };

  public distributeClientUpdate = async (appId: string) => {
    try {
      await this.httpClient.request(
        distribute({
          payloads: [
            {
              id: appId,
              messageType: 'MANIFEST_UPDATE',
              data: {
                appId,
              },
            },
          ],
          branchId: '00000000-0000-0000-0000-000000000000',
        })
      );
    } catch (e) {
      throw new CliError({
        code: CliErrorCode.FailedToDistributeClientManifestUpdate(),
        cause: e,
      });
    }
  };
}
